/**
 * Trained skills (v0.79, per Javi): earned at 🎓 Training missions —
 * a long, riskless side mission (one creature, once per act) offering a
 * pick of 1 from 3 of these. POWERFUL by design: several ramp over the
 * run or combo with relics/passives into chase-worthy setups.
 * Hooks live in utils/battle.ts + useEncounter (search the skill id).
 *
 * 🎓 v4.16 (per Javi): no longer roster-unique — two creatures may each
 * learn the same skill (a creature still never learns one twice, and a
 * skill leaves the curriculum only once EVERYONE knows it). Team-wide
 * effects apply ONCE per team, never per carrier: Quartermaster opens
 * one slot and Martyr gifts +1 no matter how many carriers share the
 * mission. Personal effects (Overclock, Star Pupil, Pacesetter's own
 * stats…) are each carrier's own, as any stat is.
 */
export interface TrainedSkillDef {
  id: string
  name: string
  /** 🎨 Painted icon (v1.91): shown wherever the skill appears. */
  image?: string
  icon: string
  /** Player-facing effect text. */
  blurb: string
  /** One-line combo/ramp hint shown in the codex. */
  combo?: string
}

// v1.12 (per Javi): Echo Training was absorbed INTO Rallying Cry — the
// standalone version felt weak next to it. Old saves migrate the
// learned id in normalizeRun; the surge-reset hooks answer to
// 'rallying-cry' only.
export const TRAINED_SKILLS: Record<string, TrainedSkillDef> = {
  // ---- 🎗️ The keepsake lesson (v6.54) - does nothing; see data/keepsakes ----
  'keepsake-lesson': {
    id: 'keepsake-lesson',
    name: "A Friend's Lesson",
    icon: '📖',
    blurb: 'Something a friend once showed you. It does nothing at all.',
  },
  // 🧬 Creature-expansion skills (v2.82) — §8 of the roster proposal.
  'avarice': {
    id: 'avarice',
    name: 'Avarice',
    icon: '🤑',
    blurb: 'At the end of every node, PERMANENTLY gains +1 to its highest element per 25 gold you are holding.',
    combo: 'Turns a hoard into a statline — with Midas Charm and Prospector it is an economy build.',
  },
  'understudy': {
    id: 'understudy',
    name: 'Understudy',
    icon: '🎭',
    blurb: "At the end of a node, PERMANENTLY copies a teammate's passive — replacing its own.",
    combo: 'Build assembly: put the passive you actually want onto the creature you actually level.',
  },
  'martyr': {
    id: 'martyr',
    name: 'Martyr',
    icon: '🩸',
    blurb: 'When a mission it joins FAILS, every OTHER creature permanently gains +1 to its highest element AND its lowest above 0. A regular expiry pays only the highest — and a free gold lapse pays nothing.',
    combo: 'A dedicated sacrifice engine — pair it with Thornmail and Stoneguard and losses stop costing you anything.',
  },
  'quartermaster': {
    id: 'quartermaster',
    name: 'Quartermaster',
    icon: '📦',
    blurb: 'Every mission it is put on gains +1 slot on the spot — room for one more body.',
    combo: 'Stacking slots — with Purpose Charm, six-body missions stop being a fantasy.',
  },
  'overclock': {
    id: 'overclock',
    name: 'Overclock',
    icon: '📈',
    blurb: 'After every successful mission, PERMANENTLY gains +1 to its highest element.',
    combo: 'A ramp that never stops — feed it missions and watch it outgrow the run.',
  },
  'trailblazer': {
    id: 'trailblazer',
    name: 'Trailblazer',
    icon: '🥾',
    blurb: 'Teams it joins DEPLOY instantly — no travel out. The walk home is normal.',
    combo: 'Far, rich missions start the moment you send them — every ramp loves the extra dispatches.',
  },
  'aegis': {
    id: 'aegis',
    name: 'Aegis',
    icon: '🛡️',
    blurb: 'Missions it joins take NO chip damage on a win, no matter the missing chance.',
    combo: 'License to gamble: risky dispatches become free rolls with Desperation Coin.',
  },
  'mentor': {
    id: 'mentor',
    name: 'Mentor',
    icon: '🎓',
    blurb: 'Teammates on its missions gain +3 bonus XP whenever the mission succeeds.',
    combo: 'Stacks with Short Fuse and Study Group into an XP fountain for the whole team.',
  },
  'colossus': {
    id: 'colossus',
    name: 'Colossus',
    icon: '💪',
    blurb: 'Its highest ALLOCATED element counts double — a steady green bonus that grows only when you spend points there.',
    combo: 'Specialists become walls; monstrous with Apex Crest and Specialist Badge solos.',
  },
  'field-medic': {
    id: 'field-medic',
    name: 'Field Medic',
    icon: '🩹',
    blurb: 'While it rests at home: every FAILED mission heals you its LEVEL in HP (expiries heal half), and once per node it grows your max HP by its level.',
    combo: 'A reason to bench your medic — turns bad days into sustain.',
  },
  'gamblers-soul': {
    id: 'gamblers-soul',
    name: "Gambler's Soul",
    icon: '🎰',
    blurb: 'Missions it joins below 60% chance are raised toward 60% (up to +15).',
    combo: 'With Desperation Coin, nothing it touches sits below 60 — and Risk Taker pays the XP.',
  },
  'star-pupil': {
    id: 'star-pupil',
    name: 'Star Pupil',
    icon: '🌟',
    blurb: 'Gains DOUBLE XP from every source.',
    combo: 'Races to level 9 passives; absurd with Field Promotion top-ups and Mentor feeds.',
  },
  'golden-touch': {
    id: 'golden-touch',
    name: 'Golden Touch',
    icon: '👑',
    blurb: 'Missions it joins pay +5 gold on success.',
    combo: 'Midas Charm DOUBLES it — a shop-funding machine on every dispatch.',
  },
  // ---- v0.81 expansion (Javi's designs + the chase batch) ----
  'momentum': {
    id: 'momentum',
    name: 'Momentum',
    icon: '💨',
    blurb: 'Each success: +1 ⚔️ Fight, but −1 to its lowest element; the first stack also brings +1 speed. Stacks to 5 — with Impulse trained too, both reaching 5 lifts the cap for both. Stacks persist between nodes; only its own failure drops them.',
    combo: 'Spirals into blitz dispatches — offset the stat drain with charms and Balanced Training. Pair with Impulse for the infinite ride.',
  },
  'impulse': {
    id: 'impulse',
    name: 'Impulse',
    icon: '⚓',
    blurb: 'Each success: +1 ⚔️ Fight, +1 to its HIGHEST element and +1 to its LOWEST above 0; the first stack also costs −1 speed. Stacks to 5 — with Momentum trained too, both reaching 5 lifts the cap for both. Stacks persist between nodes; only its own failure drops them.',
    combo: 'The slow juggernaut becomes a rainbow wall — Current Rider teammates carry its pace. Pair with Momentum for the infinite ride.',
  },
  'second-calling': {
    id: 'second-calling',
    name: 'Second Calling',
    icon: '🌗',
    blurb: 'From level 5, it ALSO awakens the passive of its strongest element BEYOND its nature — even while its natural passive lies dormant. A creature born with its OWN passive instead calls its base element\'s passive.',
    combo: 'Two passives on one creature — sculpt the second stat to pick your bonus power.',
  },
  'tough-learner': {
    id: 'tough-learner',
    name: 'Tough Learner',
    icon: '📖',
    blurb: 'Passive: any mission it joins that FAILS teaches it +5 XP — and any regular mission expiring teaches it +3.',
    combo: 'Failure farming — Star Pupil doubles the payout, Safety Net eats the sting.',
  },
  'pain-into-power': {
    id: 'pain-into-power',
    name: 'Pain Into Power',
    icon: '🔥',
    blurb: 'Whenever a mission it joins FAILS, it PERMANENTLY gains +2 to ALL elements — +1 to all when a regular mission expires and actually draws blood (a free gold lapse pays nothing). Passive — always on.',
    combo: 'Fail early to snowball later — Safety Net and Stoneguard make losses cheap.',
  },
  'rallying-cry': {
    id: 'rallying-cry',
    name: 'Rallying Cry',
    icon: '📣',
    blurb: 'Its own Surge is ALWAYS armed and never cools down — and every mission it joins resets every other creature\'s Surge (teammates fighting beside it included).',
    combo: 'The whole roster surges every wave — wild with Dual Surge statlines.',
  },
  'standard-bearer': {
    id: 'standard-bearer',
    name: 'Standard Bearer',
    icon: '🚩',
    blurb: 'Its SOLO successes count DOUBLE toward the node ⭐ goal.',
    combo: 'Crowns the Colossus/Apex Crest solo build — half the fights per node.',
  },
  'blood-pact': {
    id: 'blood-pact',
    name: 'Blood Pact',
    icon: '🩸',
    blurb: 'Optional at dispatch: pay 2 HP for +10% success chance (never on bosses).',
    combo: 'Full act heals and Field Medic refund the cost — HP becomes a spendable resource.',
  },
  'grudge': {
    id: 'grudge',
    name: 'Grudge',
    icon: '😤',
    blurb: 'After any mission fails or expires, its next dispatch gets +20% chance (never on bosses).',
    combo: 'Stacks with Team Journal\'s +10% prime into a +30% redemption swing.',
  },
  'deep-roots': {
    id: 'deep-roots',
    name: 'Deep Roots',
    icon: '🌳',
    blurb: 'Gains 1 XP every 2 seconds it rests at home on regular combat nodes.',
    combo: 'The bench that grows — Star Pupil doubles it; Mentor feeds it when it does go out.',
  },
  'pacesetter': {
    id: 'pacesetter',
    name: 'Pacesetter',
    icon: '🥁',
    blurb: 'Grants +6 ⚔️ Fight while on a team, and +2 👟 Speed to itself.',
    combo: 'Fights melt and the convoy moves quicker both ways — more dispatches per node feeds every ramp.',
  },
  'golden-standard': {
    id: 'golden-standard',
    name: 'Golden Standard',
    icon: '🏅',
    blurb: 'Gold missions it joins ALSO count toward the node ⭐ goal.',
    combo: 'With Golden Touch and Midas, gold farming stops costing tempo entirely.',
  },
  'apotheosis': {
    id: 'apotheosis',
    name: 'Apotheosis',
    icon: '🌌',
    blurb: 'Its passive awakens one more hidden tier beyond its last — far beyond anything natural.',
    combo: 'The endgame chase: with Second Calling, BOTH its passives ascend to T4.',
  },
  // ---- v2.28 (per Javi): a Surge upgrade ----
  'prism-surge': {
    id: 'prism-surge',
    name: 'Prism Surge',
    icon: '🌈',
    blurb: 'Its Surge is UPGRADED: +3 to EVERY element on the next mission — even creatures with no Surge learn one.',
    combo: 'One button answers any requirement — Rallying Cry keeps the rainbow on tap.',
  },
  'equilibrium': {
    id: 'equilibrium',
    name: 'Equilibrium',
    icon: '🧘',
    blurb: 'Toggle anytime at home: its stats present as an EVEN spread across all five elements, or flip back to its trained build (30s cooldown, total always preserved).',
    combo: 'Rainbow coverage on demand, specialist on demand — Impulse, Missing Piece and puzzle trials love the flexibility.',
  },
}

/** 🏦 Banked (v1.76, per Javi): shelved from the roll pool — defs stay
 * so saves that learned them keep working. Field Medic returned in
 * v4.65 as the lose-on-purpose kit's sustain. */
const BANKED_SKILLS = new Set(['gamblers-soul', 'standard-bearer', 'golden-standard'])
/** 🎗️ v6.54: the keepsake lesson lives in TRAINED_SKILLS (so it renders
 * and can be learned) but never in the curriculum, the codex or the
 * ceremony - it reaches a run only as the pinned fourth option of its
 * first Training Grounds (data/keepsakes). */
const HIDDEN_SKILLS = new Set(['keepsake-lesson'])


/* 🎨 Painted skill icons (v1.91, per Javi) — public/ui/skills/<id>.webp
 * for every skill, banked ones included (old saves still show them).
 * Prism Surge's crystal landed v2.31 — the full set is painted again. */
/* 🔄 In-place repaints need a cache-bust (the act-2 badge lesson) —
 * v4.61: Javi's redraws of these four. */
const REPAINTED_V2 = new Set(['avarice', 'martyr', 'quartermaster', 'understudy'])
for (const def of Object.values(TRAINED_SKILLS)) {
  def.image = `/ui/skills/${def.id}.webp${REPAINTED_V2.has(def.id) ? '?v=2' : ''}`
}

export const TRAINED_SKILL_IDS = Object.keys(TRAINED_SKILLS).filter(id => !BANKED_SKILLS.has(id) && !HIDDEN_SKILLS.has(id))

export function trainedSkill(id: string): TrainedSkillDef | null {
  return TRAINED_SKILLS[id] ?? null
}

/** 🎓 v5.86 — JAVI'S UNLOCK LADDER (from his mock, ascension column
 * per skill): the tier each skill JOINS the campaign/gauntlet
 * curriculum at. Tier 1 is the base kit (Normal and A1 play the same
 * set); unlocking ascension N adds tier N. The WEEKLY always teaches
 * the full set — shared seed, same run for everyone. Three skills
 * weren't on the mock; Claude's placements, flagged for Javi's review:
 * standard-bearer→4 (Rallying Cry combo), gamblers-soul→6 (the chaos
 * tier), golden-standard→7 (the logistics tier). */
export const SKILL_UNLOCK_TIERS: Record<string, number> = {
  'second-calling': 1,
  'star-pupil': 1,
  'mentor': 1,
  'field-medic': 2,
  'deep-roots': 2,
  'aegis': 2,
  'grudge': 3,
  'tough-learner': 3,
  'rallying-cry': 4,
  'prism-surge': 4,
  'standard-bearer': 4,
  'avarice': 5,
  'golden-touch': 5,
  'blood-pact': 5,
  'momentum': 6,
  'impulse': 6,
  'gamblers-soul': 6,
  'pacesetter': 7,
  'trailblazer': 7,
  'quartermaster': 7,
  'golden-standard': 7,
  'overclock': 8,
  'colossus': 8,
  'equilibrium': 8,
  'pain-into-power': 9,
  'martyr': 9,
  'understudy': 10,
  'apotheosis': 10,
}

/** The curriculum a player with `unlocked` ascension progress draws
 * from (campaign + gauntlet; the weekly ignores this). */
export function skillsUnlockedAt(unlocked: number): string[] {
  const cap = Math.max(1, unlocked)
  return TRAINED_SKILL_IDS.filter(id => (SKILL_UNLOCK_TIERS[id] ?? 1) <= cap)
}
